import { z } from 'zod';

import { USER_FEATURE_FLAG_KEYS, USER_FEATURE_FLAGS } from './constants';

/**
 * An object schema with one key per user feature flag, each validated by the schema
 * given for its kind. Every other key is stripped.
 */
export function createUserFeatureFlagsSchema<
  TSchemaByKind extends Record<
    (typeof USER_FEATURE_FLAGS)[keyof typeof USER_FEATURE_FLAGS]['kind'],
    z.ZodType
  >,
>(schemaByKind: TSchemaByKind) {
  return z.object(
    Object.fromEntries(
      USER_FEATURE_FLAG_KEYS.map((flag) => [
        flag,
        schemaByKind[USER_FEATURE_FLAGS[flag].kind],
      ]),
    ) as {
      [
        TFlag in keyof typeof USER_FEATURE_FLAGS
      ]: TSchemaByKind[(typeof USER_FEATURE_FLAGS)[TFlag]['kind']];
    },
  );
}

/**
 * The settings stored on a user, as the vadmin settings endpoints return them. Only the
 * user feature flags are modeled; a missing flag has its default or inherits the company
 * account (see `USER_FEATURE_FLAGS`).
 */
export const zodUserSettingsAdminSchema = z.object({
  featureFlags: createUserFeatureFlagsSchema({
    assistant: z.boolean().nullish(),
    boolean: z.boolean().optional(),
    readPath: z.boolean().nullish(),
  }).nullish(),
});

/**
 * One single sign-on link of a user's login: the account at a company identity provider
 * (e.g. the customer's Microsoft Entra ID) that signs the user in.
 */
export const zodUserIdentityProviderLinkSchema = z.object({
  externalUserId: z.string(),
  externalUsername: z.string(),
  identityProvider: z.string(),
});

/**
 * The links of a user's login, one per identity provider: the body of both the list
 * response and the response to removing one.
 */
export const zodUserIdentityProviderLinksAdminResponseSchema = z.object({
  data: z.array(zodUserIdentityProviderLinkSchema),
});
